import { Dates, Message, ThreadedMessageType, MessageThread, MessageFrom, TimesheetInput, TimesheetInputModification } from "@salaxy/core";

import { ListControllerBase, ListControllerBaseBindings } from "../bases";

import { UiHelpers, SessionService } from "../../services";

interface Weekday {
  dayName: string,
  date: string,
  displayDate: string,
  isCollapsed: boolean
}

class WorkerHourlyReportControllerBindings extends ListControllerBaseBindings {
   /** Type of the timesheet. Options are hourlyReport and timeReport */
   public reportType =  "<";
   /**
    * CRUD controller that typically defines the values for url template and
    */
    public crudController =  "<";
}

/** Controller handles timebased usecases (timer, hourlyreport) */
export class WorkerHourlyReportController extends ListControllerBase<MessageThread, Message> {
  /** Bindings for components that use this controller */
  public static bindings = new WorkerHourlyReportControllerBindings();
  /** Binded crud controller */
  public crudController: any;
  /** Date of today */
  public currentDate: string;
  public firstDayOfCurrentWeekFormatted: string;
  /** Last day of current week, formatted to MM/dd/YYYY - format */
  public lastDayOfCurrentWeekFormatted: string;
  /** first of current week, used to determine week range */
  public firstDayOfCurrentWeek: string;
  /** Lastday of current week, used to determine week range */
  public lastDayOfCurrentWeek: string;
  /** Currentweek dataset */
  public currentWeek: Weekday[] = [];
  /** Currently supported Report type, mostly affects to ui */
  public reportType: ThreadedMessageType.HourlyReport | ThreadedMessageType.TimeReport;

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["UiHelpers", "SessionService"];

  /** Constructor for dependency injection */
  constructor(
    uiHelpers: UiHelpers,
    private sessionService: SessionService,
  ) {
    super(uiHelpers);
  }

  /**
   * Implement IController
   */
  public $onInit() {
    this.currentDate = Dates.getToday();
    this.setCurrentWeek();
  }

  /**
   * List of items
   */
  public get list(): Message[] {
    if (!this.parent) {
      return null;
    }
    return this.parent.messages;
  }

  /** Template for edit UI that is shown in a modal dialog. */
  public getEditDialogTemplateUrl(): string {
    if (this.reportType == ThreadedMessageType.HourlyReport) {
      return "salaxy-components/worker/HourlyReportEditDialog.html";
    }

    if (this.reportType == ThreadedMessageType.TimeReport) {
      return "salaxy-components/worker/TimeReportEditDialog.html";
    }
  }

  /**
   * Creating of a new item
   */
  public getBlank(): Message {
    if (this.reportType === ThreadedMessageType.HourlyReport) {
      const contentData: TimesheetInput = {
        modification: TimesheetInputModification.CREATE,
        project: "",
        projectText: "",
        period: {
          start: this.currentDate
        },
        totalTime: 450
      };
      return {
        type: ThreadedMessageType.HourlyReport,
        content: "Tuntikirjaus",
        contentData,
        fromParty: MessageFrom.OtherParty,
      };
    }
    if (this.reportType === ThreadedMessageType.TimeReport) {
      const contentData: TimesheetInput = {
        modification: TimesheetInputModification.CREATE,
        project: "",
        projectText: "",
        period: {
          start: this.currentDate
        }
      };
      return {
        type: ThreadedMessageType.TimeReport,
        content: "Työaikakirjaus",
        contentData,
        fromParty: MessageFrom.OtherParty,
      };
    }
  }

  /** Logic used in ListControlerBase showEditDialog */
  public getEditDialogLogic(): any {
    return {};
  }

  /** Sums daily total time */
  public countDailyHours(date: string): number {
    let total = 0;
    const dailyMessages = this.list.filter(x => Dates.getFormattedDate(x.contentData.period.start) === Dates.getFormattedDate(date));
    dailyMessages.forEach(message => {
      total = total + message.contentData.totalTime;
    });
    if (total === 0 ) {
      return 0;
    }
    return total;
  }

  /** Sum weekly totalTime */
  public get weeklyHours(): number {
    let total = 0;
    const dailyMessages = this.list.filter(x => Dates.isSameOrAfter(x.contentData.period.start, this.firstDayOfCurrentWeek) && Dates.isSameOrBefore(x.contentData.period.start, this.lastDayOfCurrentWeek));
    dailyMessages.forEach(message => {
      total = total + message.contentData.totalTime;
    });
    if (total === 0 ) {
      return 0;
    }
    return total;
  }


  /** Sets current week for ui */
  public setCurrentWeek(): void {
    this.firstDayOfCurrentWeekFormatted = Dates.getFormattedDate(Dates.startEnd(this.currentDate, "start-week"));
    this.lastDayOfCurrentWeekFormatted = Dates.getFormattedDate(Dates.startEnd(this.currentDate, "end-week"));
    this.firstDayOfCurrentWeek = Dates.startEnd(this.currentDate, "start-week");
    this.lastDayOfCurrentWeek = Dates.startEnd(this.currentDate, "end-week");
    this.currentWeek = [
      {
        dayName: "Ma",
        date: Dates.format(this.firstDayOfCurrentWeek, "YYYY-MM-DD"),
        displayDate: Dates.format(this.firstDayOfCurrentWeek, "DD.MM"),
        isCollapsed: true
      },
      {
        dayName: "Ti",
        date: Dates.format(Dates.add(this.firstDayOfCurrentWeek, 1, "day"), "YYYY-MM-DD"),
        displayDate: Dates.format(Dates.add(this.firstDayOfCurrentWeek, 1, "day"), "DD.MM"),
        isCollapsed: true
      },
      {
        dayName: "Ke",
        date: Dates.format(Dates.add(this.firstDayOfCurrentWeek, 2, "day"), "YYYY-MM-DD"),
        displayDate: Dates.format(Dates.add(this.firstDayOfCurrentWeek, 2, "day"), "DD.MM"),
        isCollapsed: true
      },
      {
        dayName: "To",
        date: Dates.format(Dates.add(this.firstDayOfCurrentWeek, 3, "day"), "YYYY-MM-DD"),
        displayDate: Dates.format(Dates.add(this.firstDayOfCurrentWeek, 3, "day"), "DD.MM"),
        isCollapsed: true
      },
      {
        dayName: "Pe",
        date: Dates.format(Dates.add(this.firstDayOfCurrentWeek, 4, "day"), "YYYY-MM-DD"),
        displayDate: Dates.format(Dates.add(this.firstDayOfCurrentWeek, 4, "day"), "DD.MM"),
        isCollapsed: true
      },
      {
        dayName: "La",
        date: Dates.format(Dates.add(this.firstDayOfCurrentWeek, 5, "day"), "YYYY-MM-DD"),
        displayDate: Dates.format(Dates.add(this.firstDayOfCurrentWeek, 5, "day"), "DD.MM"),
        isCollapsed: true
      },
      {
        dayName: "Su",
        date: Dates.format(Dates.add(this.firstDayOfCurrentWeek, 6, "day"), "YYYY-MM-DD"),
        displayDate: Dates.format(Dates.add(this.firstDayOfCurrentWeek, 6, "day"), "DD.MM"),
        isCollapsed: true
      }
    ];
  }

  /** Gets the project text for a project */
  public getDimensionOptionText(value: string): string {
    if (value) {
      const lk1 = (this.crudController?.employingRelation?.settings?.costAccounting?.dimensions ||
        this.sessionService.session.settings?.costAccounting?.dimensions || undefined)
      .find((x) => x.name = "lk1");
      if (lk1) {
          return lk1.options.find( (x) => x.value === value)?.text ?? "";
      }
    }
    return "";
  }


  /**
   * Controlls visibility of dates messages
   * @param date Used to find correct date from currentWeek
   */
  public setHourlyRecordsVisible(date: string): void {
    const targetDate = this.currentWeek.find(x => x.date === date);
    targetDate.isCollapsed = !targetDate.isCollapsed;
  }

  /** Change current week and reload week data */
  public changeWeek(next: boolean): void {
    if (next) {
      this.currentDate = Dates.add(this.currentDate, 7, "day");
    } else {
      this.currentDate = Dates.add(this.currentDate, -7, "day");
    }
    this.setCurrentWeek();
  }

  /** Get current weeknumber */
  public get currentWeekNumber(): number {
    return Dates.getWeekNumber(this.currentDate);
  }

  /**
   * Check if provided date is saturday or sunday or not workday
   * @param date Used date to determine if saturday or sunday
   * @returns Boolean if date is saturday or sunday
   */
  public isHoliday(date: string): boolean {
    return !Dates.isWorkday(date) || Dates.isHoliday(date);
  }

  /**
   * Checks if expand button should be visible
   * @param date used to search correct objects
   * @returns Boolean that indicates if expand chevron should be visible
   */
  public showExpandButton(date: string): boolean {
      const messages = this.list.filter(x => Dates.getFormattedDate(x.contentData.period.start) === Dates.getFormattedDate(date));
      return messages.length > 0;
  }

  /**
   * Get blank message object for edit dialog
   * @param date is used to create start date
   * @returns Blank message object
   */
  public getEditDialogItem(date: string): Message {
    if (this.reportType === ThreadedMessageType.HourlyReport) {
      return {
        type: this.reportType,
        content: "Tuntikirjaus",
        fromParty: MessageFrom.OtherParty,
        contentData: {
          modification: TimesheetInputModification.CREATE,
          project: "",
          projectText: "",
          period: {
            start: date
          },
          totalTime: 450
        }
      };
    }

    if (this.reportType === ThreadedMessageType.TimeReport) {
      return {
        type: this.reportType,
        content: "Työaikakirjaus",
        fromParty: MessageFrom.OtherParty,
        contentData: {
          modification: TimesheetInputModification.CREATE,
          project: "",
          projectText: "",
          period: {
            start: date
          },
          totalTime: 450
        }
      };
    }
  }

  /**
   * Get button style for date. Handles dirrerent styles for workday, today, holiday
   * @param date Day used to figure date
   * @returns button class as string
   */
  public getDateStyle(date: string) {
    if (date === Dates.getToday()) {
      return "bg-info";
    }

    if (!Dates.isWorkday(date) || Dates.isHoliday(date)) {
      return "bg-primary";
    }

    return "btn btn-default";
  }

  /**
   * Override for ListControllerBase showEditDialog
   * @param item Item to edit or string "new" for creating a new one.
   * @param isNew Optional way of specifying that the item is a new item (with default values, not yet added to the list).
   * If item is string "new", this parameter will have no effect (will always be true).
   */
  public showEditDialog(item: Message | "new", isNew = false) {
    if (!item) {
      throw new Error("showEditDialog called without a parameter.");
    }
    if (item === "new") {
      isNew = true;
      item = this.getBlank();
    }

    const lk1 = (this.crudController?.employingRelation?.settings?.costAccounting?.dimensions ||
      this.sessionService.session.settings?.costAccounting?.dimensions || undefined)
    .find((x) => x.name = "lk1");

    const costAccountingSettings = lk1 ?
      {
        options: lk1.options,
        label: lk1.label,
      } : null;

    this.uiHelpers.openEditDialog(this.getEditDialogTemplateUrl(), item, {...this.getEditDialogLogic(), costAccountingSettings}).then((result) => {
      if (result.action === "ok" || result.action === "ok-no-save" ) {
        if (result.item.type === ThreadedMessageType.TimeReport) {
          const startTime = new Date(`${Dates.getToday()}T${result.item.contentData.startTime}:00`);
          const endTime = new Date(`${Dates.getToday()}T${result.item.contentData.endTime}:00`);
          result.item.contentData.totalTime = Math.floor((endTime.getTime() - startTime.getTime()) / 60000);
        }
        if (isNew) {
          this.list.push(result.item);
        }
        if (( isNew || result.hasChanges) && result.action !== "ok-no-save") {
          this.commit();
        }
      } else if (result.action === "delete" || result.action === "delete-no-save") {
        if (!isNew) {
          this.delete(item as Message);
          if (result.action !== "delete-no-save" ) {
            this.commit();
          }
        }
      } else {
        // No changes, cancel
      }
      });
  }

}
